import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  List,
  Paper,
  Box,
  Grid,
  Button,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCommentIcon from "@mui/icons-material/AddComment";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryIcon from "@mui/icons-material/History";
import {
  formatDateToShowTime,
  getLocalTime,
} from "components/common/Utils/commonUtilityFunctions";
import { useTheme } from "@mui/material/styles";
import packageJson from "../../../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";

// import {
//   accordionStyle,
//   accordionSummaryStyle,
//   accordionTitleStyle,
//   counterChipStyle,
// } from "../WorkOrderSegmentDetailsDialogStyles";
import { MobileCommentsProps } from "./interface/SegmentMobileCommentsTypes";
import {
  SegmentCommentHistory,
  SegmentComments,
} from "components/QueueBoard/models/workOrderTypes";
import { SegmentMobileCommentAPI } from "./api/segmentMobileCommentsApi";
import { User } from "components/authentication/models/authTypes";
import { hoverableStyle } from "components/common/global-style/GlobalStyling";
import { useSnackbar } from "notistack";

import localStorageHelper from "../../../../../app/localStorage";
import { ensureEditableWorkOrder } from "../../../utils/finalizationHelpers";
import {
  accordionStyle,
  accordionSummaryStyle,
  accordionTitleStyle,
  counterChipStyle,
} from "../workOrderSegmentDetailsDialogStyles";
const SegmentMobileComments = ({
  comments: initialComments,
  segmentIdentity,
  isReadOnly = false,
  invoiceDocumentIdentity,
}: MobileCommentsProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [comments, setComments] = useState<SegmentComments[]>(initialComments);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );
  const theme = useTheme();
  const [visibleHistorySet, setVisibleHistorySet] = useState<Set<number>>(
    new Set()
  );
  const [editingTextMap, setEditingTextMap] = useState<{
    [key: number]: string;
  }>({});
  const verifyEditable = async () =>
    ensureEditableWorkOrder(invoiceDocumentIdentity, enqueueSnackbar);
  // Toggle history display
  const toggleHistory = (commentId: number) => {
    setVisibleHistorySet((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId); // hide history for this comment
      } else {
        newSet.add(commentId); // show history for this comment
      }
      return newSet;
    });
  };
  //Save comment
  const handleAddComment = async () => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    if (!newComment.trim()) {
      enqueueSnackbar(
        "Please type something before clicking add comment button",
        {
          variant: "error",
        }
      );
      return;
    }
    try {
      const newItem = await SegmentMobileCommentAPI.addComment({
        comment: newComment,
        segmentIdentity: segmentIdentity,
        technicianName: user.userName,
        technicianIdentity: user.identity,
        enteredBy: user.userName,
        modifiedBy: user.userName,
        enteredDate: getLocalTime(),
        modifiedDate: getLocalTime(),
        commentActivity: "Created",
        commentIsActive: true,
      });
      setComments((prev) => [newItem as SegmentComments, ...prev]);
      setNewComment("");
      enqueueSnackbar("Comment saved successfully!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleAddComment",
      {
        elapsed_ms: "124",
        title: "Segment Mobile Comment",
        componentName: "SegmentMobileComments.tsx",
        version: packageJson.version,
      }
    );
  };

  //Start editing
  const handleEdit = (comment: SegmentComments) => {
    if (isReadOnly) return;
    setEditingId(comment.commentIdentity);
    setEditingTextMap((prev) => ({
      ...prev,
      [comment.commentIdentity]: comment.comment,
    }));
  };

  //Edit comment
  const handleSaveEdit = async (comment: SegmentComments) => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    try {
      const updatedText = editingTextMap[comment.commentIdentity];
      const modifiedTime = getLocalTime();

      //Clone the history or initialize it
      const previousHistory: SegmentCommentHistory = {
        //commentIdentity: 0,
        commentIdentity: comment.commentIdentity,
        technicianName: comment.technicianName ?? user.userName,
        activity: "Edited",
        technicianIdentity: comment.technicianIdentity ?? 0,
        comment: comment.comment,
        modifiedDate: modifiedTime,
        modifiedBy: comment.modifiedBy ?? user.userName,
        createdDate: modifiedTime,
        createdBy: comment.modifiedBy ?? user.userName,
        identity: 0,
      };

      const updatedCommentHistory: SegmentCommentHistory[] =
        comment.commentHistory
          ? [...comment.commentHistory, previousHistory]
          : [previousHistory];

      // Prepare the updated comment object
      const updatedComment = await SegmentMobileCommentAPI.updateComment({
        ...comment,
        commentActivity: "Edited",
        comment: updatedText,
        //commentHistory: updatedCommentHistory,
        segmentIdentity: segmentIdentity,
        modifiedBy: user.userName,
        modifiedDate: modifiedTime,
      });

      // Step 3: Update state
      setComments((prev) =>
        prev.map((c) =>
          c.commentIdentity === comment.commentIdentity
            ? {
                ...c,
                comment: updatedComment.comment,
                modifiedDate: modifiedTime,
                modifiedBy: user.userName,
                commentHistory: updatedCommentHistory,
              }
            : c
        )
      );

      setEditingId(null);
      enqueueSnackbar("Comment updated successfully!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleEditComment",
      {
        elapsed_ms: "124",
        title: "Segment Mobile Comment",
        componentName: "SegmentMobileComments.tsx",
        version: packageJson.version,
      }
    );
  };

  //Delete comment
  const handleDelete = async (id: number) => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    const response = await SegmentMobileCommentAPI.deleteComment(id);
    if (response) {
      enqueueSnackbar("Comment deleted successfully!", {
        variant: "success",
      });
      setComments((prev) =>
        prev.map((c) =>
          c.commentIdentity === id ? { ...c, commentIsActive: false } : c
        )
      );
    } else {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleDeleteComment",
      {
        elapsed_ms: "124",
        title: "Segment Mobile Comment",
        componentName: "SegmentMobileComments.tsx",
        version: packageJson.version,
      }
    );
  };
  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
  };
  return (
    <Accordion
      sx={{
        ...accordionStyle,
        backgroundColor: (theme) =>
          theme.palette.mode === "light" ? "#fff" : "#181F2A",
      }}
    >
      <AccordionSummary
        sx={accordionSummaryStyle}
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography gutterBottom sx={accordionTitleStyle}>
          Mobile Comments
          <Chip
            variant="outlined"
            label={comments.length}
            size="small"
            sx={counterChipStyle}
          />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box mb={2}>
          <Grid container spacing={1} alignItems="stretch">
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                size="small"
                inputProps={{ maxLength: 4000 }}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Type a comment..."
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                sx={{
                  textTransform: "none",
                  height: { xs: 40, md: "100%" },
                }}
                onClick={handleAddComment}
                startIcon={<AddCommentIcon />}
                disabled={isReadOnly}
              >
                Add Comment
              </Button>
            </Grid>
          </Grid>
        </Box>

        <List
          sx={{
            maxHeight: 200,
            overflowY: "auto",
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#fff" : "#181F2A",
          }}
        >
          {comments
            .slice()
            .sort(
              (a, b) =>
                new Date(b.modifiedDate).getTime() -
                new Date(a.modifiedDate).getTime()
            )
            .map((comment) => (
              <Paper
                key={comment.commentIdentity}
                elevation={0}
                sx={{
                  p: 1,
                  mb: 1,
                  bgcolor: comment.commentIsActive
                    ? (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "grey.100"
                    : (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.04)"
                          : "darkgray",
                }}
              >
                <Box>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item xs={6}>
                      <Typography variant="bodyBold">
                        {comment.technicianName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="bodyMuted">
                        {formatDateToShowTime(new Date(comment.modifiedDate))}
                      </Typography>
                    </Grid>
                  </Grid>

                  {editingId === comment.commentIdentity ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        inputProps={{ maxLength: 4000 }}
                        minRows={2}
                        value={editingTextMap[comment.commentIdentity] || ""}
                        onChange={(e) =>
                          setEditingTextMap((prev) => ({
                            ...prev,
                            [comment.commentIdentity]: e.target.value,
                          }))
                        }
                        sx={{ mt: 1, bgcolor: theme.palette.background.paper }}
                      />

                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={1}
                      >
                        {/* Save Comment */}
                        <Tooltip title="Save Comment">
                          <IconButton
                            onClick={() => handleSaveEdit(comment)}
                            disabled={isReadOnly}
                          >
                            <SaveIcon
                              sx={{ ...hoverableStyle, color: "green" }}
                            />
                          </IconButton>
                        </Tooltip>

                        {/* Cancel Editing */}
                        <Tooltip title="Cancel Editing">
                          <IconButton
                            onClick={handleCancelEdit}
                            disabled={isReadOnly}
                          >
                            <CancelIcon
                              sx={{ ...hoverableStyle, color: "red" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={10}>
                          <Typography variant="bodySmall" sx={{ mt: 1 }}>
                            {comment.comment}
                          </Typography>
                        </Grid>

                        {/* If comment is already removed don't show edit or remove button */}
                        {/* Comment History  */}

                        <Grid item textAlign={"right"} xs={2}>
                          {comment.commentHistory.length > 0 && (
                            <Tooltip title="Comment History">
                              <IconButton
                                onClick={() =>
                                  toggleHistory(comment.commentIdentity)
                                }
                              >
                                <HistoryIcon
                                  sx={
                                    visibleHistorySet.has(
                                      comment.commentIdentity
                                    )
                                      ? {
                                          ...hoverableStyle,
                                          color: theme.palette.primary.main,
                                        }
                                      : hoverableStyle
                                  }
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                          {comment.commentIsActive && (
                            <>
                              {/* Edit Comment */}
                              <Tooltip title="Edit Comment">
                                <span>
                                  <IconButton
                                    onClick={() => handleEdit(comment)}
                                    disabled={isReadOnly || editingId !== null}
                                  >
                                    <EditIcon
                                      sx={hoverableStyle}
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              {/* Delete Comment */}
                              <Tooltip title="Delete Comment">
                                <span>
                                  <IconButton
                                    onClick={() =>
                                      handleDelete(comment.commentIdentity)
                                    }
                                    disabled={isReadOnly}
                                  >
                                    <DeleteIcon
                                      sx={{ ...hoverableStyle, color: "red" }}
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>

                {visibleHistorySet.has(comment.commentIdentity) &&
                  comment.commentHistory.length > 0 && (
                    <Paper
                      sx={{
                        p: 1,
                        mt: 1,
                        maxHeight: 140, // Set dropdown height
                        overflowY: "auto", // Add scrollbar if needed
                      }}
                    >
                      {comment.commentHistory
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(b.modifiedDate).getTime() -
                            new Date(a.modifiedDate).getTime()
                        )
                        .map((history, index) => (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 1,
                              mb: 1,
                              bgcolor: "grey.200",
                            }}
                          >
                            <Grid
                              container
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Grid item xs={6}>
                                <Typography variant="bodyBold">
                                  {history.technicianName}
                                </Typography>
                              </Grid>
                              <Grid item textAlign={"right"} xs={6}>
                                <Typography variant="bodyMuted">
                                  {formatDateToShowTime(
                                    new Date(history.modifiedDate)
                                  )}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Typography variant="bodySmall" sx={{ mt: 1 }}>
                              {history.comment}
                            </Typography>
                          </Paper>
                        ))}
                    </Paper>
                  )}
              </Paper>
            ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default SegmentMobileComments;
